import {hasValue} from '@famly/stat_ts-utils_has-value';

/** A `Warnable` is a value that may have warnings attached. */
export type Warnable<TValue, TWarning> = ResolvedWarnable<TValue> | WarningsWarnable<TValue, TWarning>;
export type ResolvedWarnable<TValue> = {value: TValue; warnings?: never};
export type WarningsWarnable<TValue, TWarning> = {value: TValue; warnings: WarningWithId<TWarning>[]};

export type WarningWithId<TWarning> = {warning: TWarning; id: string};

/**
 * A Warnable with no warnings.
 *
 * Monadic unit over `Warnable<_, TWarning>`.
 */
export const resolve = <TValue>(value: TValue): ResolvedWarnable<TValue> => ({value});

/**
 * A shortcut for creating a Warnable with a single string warning. Uses the
 * warning itself as warning id.
 */
export const warningMessage = <TValue>(value: TValue, warning: string): Warnable<TValue, string> => ({
    value,
    warnings: [{warning, id: warning}],
});

/**
 * Produces a warnable, which is resolved with `value` if `value` is non-nullish. Otherwise falls back to `fallback` with the given warning.
 */
export const fallbackNullish = <TValue>(
    value: TValue | null | undefined,
    fallback: TValue,
    warning: string,
): Warnable<TValue, string> => (hasValue(value) ? resolve(value) : warningMessage(fallback, warning));

/** A Warnable with a warning. */
export const warning = <TValue, TWarning>(value: TValue, warning: TWarning, id: string) => ({
    value,
    warnings: [{warning, id}],
});

/** A Warnable with multiple warnings. */
export const warnings = <TValue, TWarning>(value: TValue, warnings: WarningWithId<TWarning>[]) => ({value, warnings});

/** Tests if a Warnable has no warnings. */
export const isResolved = <TValue, TWarning>(
    warnable: Warnable<TValue, TWarning>,
): warnable is ResolvedWarnable<TValue> => warnable.warnings === undefined;

/** Tests if a Warnable has warnings. */
export const isWarnings = <TValue, TWarning>(
    warnable: Warnable<TValue, TWarning>,
): warnable is WarningsWarnable<TValue, TWarning> => warnable.warnings !== undefined;

/** Gets a list of warnings in the Warnable. */
export const getWarnings = <TValue, TWarning>(warnable: Warnable<TValue, TWarning>): WarningWithId<TWarning>[] =>
    isResolved(warnable) ? [] : warnable.warnings;

/**
 * Dispatches the collected warnings (if any) to e.g. Sentry.
 *
 * @param warnable The Warnable to dispatch warnings from.
 * @param onWarnings Is called on the warnings list if there are warnings.
 * @returns The value of the Warnable.
 */
export const dispatchWarnings = <TValue, TWarning>(
    warnable: Warnable<TValue, TWarning>,
    onWarnings: (warnings: WarningWithId<TWarning>[]) => void,
): TValue => {
    if (isWarnings(warnable)) {
        onWarnings(warnable.warnings);
    }
    return warnable.value;
};
/**
 * Applies a function to the value of a Warnable.
 *
 * Keeps the warnings from the Warnable.
 *
 * Functor map over `Warnable<_, TWarning>`.
 */
export const map = <TValue1, TValue2, TWarning>(
    warnable: Warnable<TValue1, TWarning>,
    fun: (value: TValue1) => TValue2,
): Warnable<TValue2, TWarning> =>
    isResolved(warnable)
        ? {value: fun(warnable.value)}
        : {
              value: fun(warnable.value),
              warnings: warnable.warnings,
          };

/**
 * Chains dependent Warnables.
 *
 * Combines warnings from the two Warnables.
 *
 * Monadic bind over `Warnable<_, TWarning>`.
 */
export const bind = <TValue1, TValue2, TWarning>(
    warnable1: Warnable<TValue1, TWarning>,
    warnableFun2: (value: TValue1) => Warnable<TValue2, TWarning>,
): Warnable<TValue2, TWarning> => {
    const warnable2 = warnableFun2(warnable1.value);
    const warnings = combineWarnings(warnable1.warnings, warnable2.warnings);
    return hasValue(warnings) ? {value: warnable2.value, warnings} : {value: warnable2.value};
};

/** Collects an array of Warnables into a Warnable array. */
export const all = <TValue, TWarning>(array: Warnable<TValue, TWarning>[]): Warnable<TValue[], TWarning> => {
    const values = array.map(element => element.value);
    const warnings = combineWarningsList(array.map(element => element.warnings));
    return hasValue(warnings) ? {value: values, warnings} : {value: values};
};

type WarnableFields<TRecord, TWarning> = {[P in keyof TRecord]: Warnable<TRecord[P], TWarning>};

/**
 * Collects a record of Warnables into a Warnable record.
 *
 * Can be used in conjunction with `bind` or `map` to join multiple warnable
 * operations.
 * @example
 *   const name = resolve('Mathilde');
 *   const age = warning(0, 'Failed to get age');
 *   const sum = map(
 *       allFields({name, age}),
 *       ({name, age})=>({type: 'child', name, age})
 *   );
 *   assert(sum === {
 *       value: {type: 'child', name: 'Mathilde', age: 0},
 *       warnings: ['Failed to get age']
 *   });
 */
export const allFields: <TRecord, TWarning>(
    record: WarnableFields<TRecord, TWarning>,
) => Warnable<TRecord, TWarning> = (record: any): any => {
    // todo: can the implementation be made type safe?
    const values = Object.entries(record).map(([key, value]: [string, any]) => [key, value.value]);
    const warnings = Object.entries(record).reduce(
        (warnings: any[] | undefined, keyValuePair) => combineWarnings(warnings, (keyValuePair[1] as any).warnings),
        undefined,
    );
    return hasValue(warnings) ? {value: Object.fromEntries(values), warnings} : {value: Object.fromEntries(values)};
};

export const showWarningsListSummary = <TWarning>(warnings: WarningWithId<TWarning>[]): string =>
    showWarningsSummary(summarizeWarningsFromList(warnings));

/** Warnings grouped by id. */
export type WarningsSummary<TWarning> = {[id: string]: {warning: TWarning; count: number}};

/**
 * Groups warnings by warning id and calculates the number of warnings in each
 * group.
 *
 * Will pick the first warning message occurrence for each group message.
 */
export const summarizeWarnings = <TValue, TWarning>(warnable: Warnable<TValue, TWarning>): WarningsSummary<TWarning> =>
    summarizeWarningsFromList(getWarnings(warnable));

/**
 * Groups warnings by warning id and calculates the number of warnings in each
 * group.
 *
 * Will pick the first warning message occurrence for each group message.
 */
const summarizeWarningsFromList = <TWarning>(warnings: WarningWithId<TWarning>[]): WarningsSummary<TWarning> => {
    return warnings.reduce((overview: WarningsSummary<TWarning>, warning: WarningWithId<TWarning>) => {
        const overviewItem = overview[warning.id];
        if (!hasValue(overviewItem)) {
            return {...overview, [warning.id]: {warning: warning.warning, count: 1}};
        }
        return {...overview, [warning.id]: {...overviewItem, count: overviewItem.count + 1}};
    }, {});
};

/** Creates a string representation of a `WarningsOverview`. */
const showWarningsSummary = <TWarning>(
    overview: WarningsSummary<TWarning>,
    showWarning?: (warning: TWarning) => string,
): string => {
    const showWarningFun = hasValue(showWarning) ? showWarning : String;
    return [...Object.values(overview)]
        .sort((item1, item2) => item2.count - item1.count)
        .map(item =>
            item.count > 1 ? `${showWarningFun(item.warning)} (${item.count})` : showWarningFun(item.warning),
        )
        .join(', ');
};

const combineWarnings = <TWarning>(
    warnings1: TWarning[] | undefined,
    warnings2: TWarning[] | undefined,
): TWarning[] | undefined => {
    if (warnings1 === undefined) {
        return warnings2;
    }
    if (warnings2 === undefined) {
        return warnings1;
    }
    return [...warnings1, ...warnings2];
};

const combineWarningsList = <TWarning>(warningsList: (TWarning[] | undefined)[]): TWarning[] | undefined =>
    warningsList.reduce(combineWarnings, undefined);
